import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectRouteType,
    selectStartPoint,
    selectEndPoint,
    selectViaPoints,
    setStartPoint,
    setEndPoint,
    addViaPoint,
    removeViaPoint,
    clearViaPoints,
    setCurrentlyEditingPointType,
    checkLocationsStepCompleted,
    selectPlaceRoutePointOnClick,
    setPlaceRoutePointOnClick,
    selectWorkingLatLng,
    selectWorkingAddress,
} from '../../../features/tripcenter/googleRouteMakerSlice';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Existing components
import AddLocation from './AddLocation';

const LocationSetup = () => {
    const dispatch = useDispatch();
    const routeType = useSelector(selectRouteType);
    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const placeRoutePointOnClick = useSelector(selectPlaceRoutePointOnClick);

    // States for location editing
    const [addingLocationType, setAddingLocationType] = useState(null);
    const [editingViaIndex, setEditingViaIndex] = useState(null);

    // Update completion status when locations change
    useEffect(() => {
        dispatch(checkLocationsStepCompleted());
    }, [startPoint, endPoint, viaPoints, dispatch]);




    const handleLocationComplete = (locationData) => {
        //console.log("LocationSetup received location data:", locationData);

        // No need to validate here since AddLocation already validated
        if (addingLocationType === 'start') {
            dispatch(setStartPoint(locationData));
        } else if (addingLocationType === 'end') {
            dispatch(setEndPoint(locationData));
        } else if (addingLocationType === 'via') {
            if (editingViaIndex !== null) {
                dispatch(removeViaPoint(editingViaIndex));
            }
            dispatch(addViaPoint(locationData));
        }

        // Reset states
        setAddingLocationType(null);
        setEditingViaIndex(null);
        dispatch(setPlaceRoutePointOnClick(false));
    };

    // Remove a via point
    const handleRemoveViaPoint = (index) => {
        dispatch(removeViaPoint(index));
    };

    // Clear all via points
    const handleClearViaPoints = () => {
        dispatch(clearViaPoints());
    };

    // Get location setup steps based on route type
    const getLocationSteps = () => {
        switch (routeType) {
            case 'oneWayDirect':
                return [
                    { key: 'start', label: 'Start Location' },
                    { key: 'end', label: 'End Location' }
                ];
            case 'roundTrip':
                return [
                    { key: 'start', label: 'Start Location' },
                    { key: 'end', label: 'Destination' }
                ];
            case 'oneWayWithStops':
                return [
                    { key: 'start', label: 'Start Location' },
                    { key: 'via', label: 'Via Points' },
                    { key: 'end', label: 'End Location' }
                ];
            case 'roundTripWithStops':
                return [
                    { key: 'start', label: 'Start Location' },
                    { key: 'via', label: 'Via Points' },
                    { key: 'end', label: 'Destination' }
                ];
            default:
                return [];
        }
    };

    // Starting adding a new location
    const handleAddLocation = (locationType, viaIndex = null) => {
        setAddingLocationType(locationType);
        setEditingViaIndex(viaIndex);

        if (locationType === 'start') {
            dispatch(setCurrentlyEditingPointType('start'));
        } else if (locationType === 'end') {
            dispatch(setCurrentlyEditingPointType('end'));
        } else if (locationType === 'via') {
            dispatch(setCurrentlyEditingPointType('via'));
        }
    };

    // Cancel adding a location
    const handleCancelAddLocation = () => {
        // Turn off map selection if it was active
        if (placeRoutePointOnClick) {
            dispatch(setPlaceRoutePointOnClick(false));
        }

        setAddingLocationType(null);
        setEditingViaIndex(null);
    };

    // Render location list
    const renderLocationsList = () => {
        return (
            <List sx={{
                width: '100%',
                '& .MuiListItem-root': {
                    borderRadius: 1,
                    my: 0.5,
                    px: 2,
                    py: 1.5,
                    transition: 'background-color 0.2s'
                }
            }}>
                {/* Start Point */}
                <ListItem
                    secondaryAction={
                        <IconButton
                            edge="end"
                            onClick={() => handleAddLocation('start')}
                            color="primary"
                        >
                            {startPoint ? <EditIcon /> : <AddIcon />}
                        </IconButton>
                    }
                    sx={{
                        bgcolor: startPoint ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                        border: startPoint ? '1px solid rgba(25, 118, 210, 0.1)' : 'none'
                    }}
                >
                    <ListItemIcon>
                        <TripOriginIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                    Start Location
                                </Typography>
                                {startPoint && (
                                    <CheckCircleIcon
                                        color="primary"
                                        fontSize="small"
                                        sx={{ ml: 1, opacity: 0.8 }}
                                    />
                                )}
                            </Box>
                        }
                        secondary={
                            startPoint ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 0.5,
                                        color: 'text.primary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {startPoint.locationType === 'zone' || startPoint.locationType === 'marker' ?
                                        startPoint.locationName :
                                        startPoint.address}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Not set
                                </Typography>
                            )
                        }
                    />
                </ListItem>

                {/* Via Points - only for routes with stops */}
                {(routeType === 'oneWayWithStops' || routeType === 'roundTripWithStops') && (
                    <>
                        <Divider sx={{ my: 1.5 }} />

                        {viaPoints.length > 0 ? (
                            viaPoints.map((point, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <Box>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleAddLocation('via', index)}
                                                    sx={{ mr: 1 }}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleRemoveViaPoint(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        }
                                        sx={{
                                            bgcolor: 'rgba(156, 39, 176, 0.04)',
                                            border: '1px solid rgba(156, 39, 176, 0.1)'
                                        }}
                                    >
                                        <ListItemIcon>
                                            <AddLocationAltIcon color="info" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                        Via Point {index + 1}
                                                    </Typography>
                                                    <CheckCircleIcon
                                                        color="info"
                                                        fontSize="small"
                                                        sx={{ ml: 1, opacity: 0.8 }}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 0.5,
                                                        color: 'text.primary',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {point.locationType === 'zone' || point.locationType === 'marker' ?
                                                        point.locationName :
                                                        point.address}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {index < viaPoints.length - 1 && <Box sx={{ height: 8 }} />}
                                </React.Fragment>
                            ))
                        ) : (
                            <ListItem
                                sx={{
                                    bgcolor: 'background.paper',
                                    border: '1px dashed rgba(0, 0, 0, 0.12)',
                                    borderRadius: 1
                                }}
                            >
                                <ListItemIcon>
                                    <AddLocationAltIcon color="disabled" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="No Via Points Added"
                                    secondary="Add at least one stop between start and end"
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddLocation('via')}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Stop
                                </Button>
                            </ListItem>
                        )}

                        {viaPoints.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mt: 2, mb: 1 }}>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddLocation('via')}
                                    size="small"
                                    variant="text"
                                    color="primary"
                                >
                                    Add Another Stop
                                </Button>

                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={handleClearViaPoints}
                                    size="small"
                                    variant="text"
                                    color="error"
                                >
                                    Clear All Stops
                                </Button>
                            </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                    </>
                )}

                {/* End Point */}
                <ListItem
                    secondaryAction={
                        <IconButton
                            edge="end"
                            onClick={() => handleAddLocation('end')}
                            color={endPoint ? "primary" : "error"}
                        >
                            {endPoint ? <EditIcon /> : <AddIcon />}
                        </IconButton>
                    }
                    sx={{
                        bgcolor: endPoint ? 'rgba(244, 67, 54, 0.04)' : 'rgba(244, 67, 54, 0.02)',
                        border: endPoint ?
                            '1px solid rgba(244, 67, 54, 0.1)' :
                            '1px dashed rgba(244, 67, 54, 0.2)',
                        borderRadius: 1
                    }}
                >
                    <ListItemIcon>
                        <LocationOnIcon color={endPoint ? "secondary" : "error"} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: endPoint ? 'medium' : 'normal' }}
                                >
                                    {routeType === 'roundTrip' || routeType === 'roundTripWithStops'
                                        ? 'Destination'
                                        : 'End Location'}
                                </Typography>
                                {endPoint && (
                                    <CheckCircleIcon
                                        color="secondary"
                                        fontSize="small"
                                        sx={{ ml: 1, opacity: 0.8 }}
                                    />
                                )}
                                {!endPoint && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            ml: 1,
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: '12px',
                                            fontSize: '0.7rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Required
                                    </Typography>
                                )}
                            </Box>
                        }
                        secondary={
                            endPoint ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 0.5,
                                        color: 'text.primary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {endPoint.locationType === 'zone' || endPoint.locationType === 'marker' ?
                                        endPoint.locationName :
                                        endPoint.address}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Not set - please select a destination
                                </Typography>
                            )
                        }
                    />
                </ListItem>
            </List>
        );
    };

    // Add this to determine if we're in location selection mode
    const isSelectingLocation = addingLocationType || placeRoutePointOnClick;

    // If no route type is selected, show error
    if (!routeType) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Please select a route type first before setting up locations.
                </Alert>
            </Box>
        );
    }

    // Regular expanded view - modify the return statement
    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Set Up Route Locations
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {routeType === 'oneWayDirect'
                    ? 'Add a start location and an end location for your route.'
                    : routeType === 'roundTrip'
                        ? 'Add your start location and destination. The route will automatically return to the start.'
                        : routeType === 'oneWayWithStops'
                            ? 'Add a start location, stops along the way, and a final destination.'
                            : 'Add a start location, stops along the way, and a destination. The route will return to the start.'}
            </Typography>

            {/* Show either the location selection UI or the locations list */}
            {isSelectingLocation ? (
                <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                    <AddLocation
                        onLocationComplete={handleLocationComplete}
                        onCancel={handleCancelAddLocation}
                    />
                </Paper>
            ) : (
                <>
                    <Paper variant="outlined" sx={{ mt: 3 }}>
                        {renderLocationsList()}
                    </Paper>

                    <Box sx={{ mt: 4 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Location Setup Progress:
                        </Typography>
                        <Stepper
                            activeStep={startPoint ? (endPoint ? 2 : 1) : 0}
                            alternativeLabel
                            sx={{
                                '& .MuiStepLabel-root': {
                                    transition: 'all 0.2s'
                                },
                                '& .MuiStepLabel-active': {
                                    fontWeight: 'bold'
                                },
                                '& .MuiStepLabel-completed': {
                                    color: 'success.main'
                                }
                            }}
                        >
                            {getLocationSteps().map((step) => (
                                <Step
                                    key={step.key}
                                    completed={
                                        (step.key === 'start' && !!startPoint) ||
                                        (step.key === 'end' && !!endPoint) ||
                                        (step.key === 'via' && viaPoints.length > 0)
                                    }
                                >
                                    <StepLabel
                                        optional={
                                            (step.key === 'start' && startPoint) ?
                                                <Typography variant="caption" color="success.main">Completed</Typography> :
                                                (step.key === 'end' && !endPoint) ?
                                                    <Typography variant="caption" color="error.main">Required</Typography> :
                                                    (step.key === 'via' && viaPoints.length === 0 && (routeType === 'oneWayWithStops' || routeType === 'roundTripWithStops')) ?
                                                        <Typography variant="caption" color="primary.main">Required</Typography> :
                                                        undefined
                                        }
                                    >
                                        {step.label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default LocationSetup; 